/**
 * DropboxNotificationBell — unified owner notification center.
 *
 * Sits beside "Reports / Help". Badge shows total unseen; clicking opens a modal
 * with tabbed navigation (All / Documents / Reservations) and per-type unseen
 * counts. Feed (last 14 days for the selected, access-vetted property) unions:
 *   - document changes (Dropbox tracker)
 *   - new reservations + cancellations (sourced from CLC Communications' queue)
 * The default view shows unread items; read items remain available through its
 * filter. Each row has a "seen" checkbox; opening a document marks it read,
 * while reservations open the in-portal folio.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, FileText, FileImage, FileVideo, File as FileIcon, CalendarPlus, CalendarX2, Loader2, AlertCircle } from 'lucide-react';
import { getOwnerDropboxNotifications, ackOwnerNotification, getOwnerDropboxFileBlob, getOwnerDropboxFileUrl } from '../../services/ownerApi.js';
import { useSession } from '../../contexts/SessionContext.jsx';
import { useIsNarrowViewport } from '../../utils/useIsNarrowViewport.js';

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'avif']);
const PDF_EXTS = new Set(['pdf']);
const VIDEO_EXTS = new Set(['mp4', 'mov', 'm4v', 'webm']);
const extOf = (n) => String(n || '').split('.').pop().toLowerCase();
const kindOf = (n) => (PDF_EXTS.has(extOf(n)) ? 'pdf' : IMAGE_EXTS.has(extOf(n)) ? 'image' : VIDEO_EXTS.has(extOf(n)) ? 'video' : null);
const fileIconFor = (n) => (IMAGE_EXTS.has(extOf(n)) ? FileImage : VIDEO_EXTS.has(extOf(n)) ? FileVideo : PDF_EXTS.has(extOf(n)) ? FileText : FileIcon);
const folderOf = (rel) => { const p = String(rel || '').split('/'); p.pop(); return p.join('/'); };
const CHANGE_LABEL = { added: 'Added', modified: 'Updated', removed: 'Removed' };
const CHANGE_COLOR = { added: '#067647', modified: '#b54708', removed: '#b42318' };
const isReservation = (t) => t === 'reservation' || t === 'cancellation';

function fmtDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
}
function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }); } catch { return ''; }
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'document', label: 'Documents' },
  { key: 'reservation', label: 'Reservations' },
  { key: 'cancellation', label: 'Cancellations' },
];

export default function DropboxNotificationBell({ onOpenReservation }) {
  const { selectedProperty } = useSession();
  const isNarrow = useIsNarrowViewport();
  const listingId = selectedProperty?.listing_id;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [tab, setTab] = useState('all');
  const [visibility, setVisibility] = useState('unread');
  const [sortKey, setSortKey] = useState('unseen');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const previewUrlRef = useRef(null);

  const load = useCallback(async () => {
    if (!listingId) { setItems([]); setUnseen(0); return; }
    setLoading(true);
    try {
      const res = await getOwnerDropboxNotifications({ listing_id: listingId });
      setItems(res?.notifications || []);
      setUnseen(res?.unseen_count || 0);
    } catch {
      /* nav badge fails silent */
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current); }, []);

  const tabUnseen = useCallback((key) => items.filter((n) => (key === 'all' ? true : n.type === key) && !n.seen).length, [items]);

  const visible = useMemo(() => {
    const filtered = items.filter((n) => (
      (tab === 'all' ? true : n.type === tab)
      && (visibility === 'all' ? true : visibility === 'read' ? n.seen : !n.seen)
    ));
    const dateDesc = (a, b) => String(b.detected_at).localeCompare(String(a.detected_at));
    const arr = [...filtered];
    switch (sortKey) {
      case 'newest': arr.sort(dateDesc); break;
      case 'oldest': arr.sort((a, b) => String(a.detected_at).localeCompare(String(b.detected_at))); break;
      case 'property': arr.sort((a, b) => (a.property_name || '').localeCompare(b.property_name || '') || dateDesc(a, b)); break;
      case 'unseen':
      default: arr.sort((a, b) => (a.seen === b.seen ? dateDesc(a, b) : (a.seen ? 1 : -1))); break;
    }
    return arr;
  }, [items, tab, visibility, sortKey]);

  const markSeen = useCallback(async (n) => {
    if (n.seen) return;
    setItems((prev) => prev.map((x) => (x.key === n.key ? { ...x, seen: true } : x)));
    setUnseen((c) => Math.max(0, c - 1));
    try {
      const res = await ackOwnerNotification(n.key, true, listingId);
      if (res && typeof res.unseen_count === 'number') setUnseen(res.unseen_count);
    } catch {
      load();
    }
  }, [listingId, load]);

  const toggleSeen = useCallback(async (n) => {
    if (!n.seen) {
      await markSeen(n);
      return;
    }
    const newSeen = !n.seen;
    setItems((prev) => prev.map((x) => (x.key === n.key ? { ...x, seen: newSeen } : x)));
    setUnseen((c) => Math.max(0, c + (newSeen ? -1 : 1)));
    try {
      const res = await ackOwnerNotification(n.key, newSeen, listingId);
      if (res && typeof res.unseen_count === 'number') setUnseen(res.unseen_count);
    } catch {
      load();
    }
  }, [load, listingId, markSeen]);

  const closePreview = useCallback(() => {
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
    setPreview(null);
  }, []);

  const openDocument = useCallback(async (n) => {
    // Opening a document is a read action. Do not block the preview on the
    // acknowledgement request; a transient failure reloads the authoritative state.
    void markSeen(n);
    const kind = kindOf(n.file_name);
    // Video: stream from a direct same-origin URL (HTTP Range) instead of buffering
    // the whole file into a blob — large videos otherwise fail on iOS Safari.
    if (kind === 'video') {
      if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
      setPreview({ name: n.file_name, kind, url: getOwnerDropboxFileUrl(n.hostaway_listing_id, n.rel_path), loading: false });
      return;
    }
    setPreview({ name: n.file_name, kind: kind || 'file', url: null, loading: true });
    try {
      const result = await getOwnerDropboxFileBlob(n.hostaway_listing_id, n.rel_path, { download: !kind });
      if (!result || !kind) { closePreview(); return; }
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(result.blob);
      previewUrlRef.current = url;
      setPreview({ name: n.file_name, kind, url, loading: false });
    } catch (err) {
      setPreview({ name: n.file_name, kind: kind || 'file', url: null, loading: false, error: err?.message || 'Preview failed' });
    }
  }, [closePreview, markSeen]);

  const toggleOpen = useCallback(() => {
    setOpen((wasOpen) => {
      const willOpen = !wasOpen;
      if (willOpen) void load();
      return willOpen;
    });
  }, [load]);

  const openReservation = useCallback((n) => {
    setOpen(false);
    if (onOpenReservation) {
      onOpenReservation({
        reservation_id: n.reservation_id,
        listing_id: n.hostaway_listing_id,
        guest_name: n.guest_name,
        channel: n.channel,
        channel_code: n.channel,
        check_in: n.checkin,
        check_out: n.checkout,
      });
    }
  }, [onOpenReservation]);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
      <button type="button" onClick={toggleOpen} title="Notifications" aria-label="Notifications" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'inline-flex', alignItems: 'center', padding: '0.2rem', position: 'relative' }}>
        <Bell size={18} strokeWidth={2} />
        {unseen > 0 && (
          <span style={{ position: 'absolute', top: '-4px', right: '-4px', minWidth: '16px', height: '16px', padding: '0 4px', borderRadius: '999px', background: '#e11d48', color: '#fff', fontSize: '0.65rem', fontWeight: 700, lineHeight: '16px', textAlign: 'center' }}>
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>

      {open && createPortal((
        <div role="dialog" aria-modal="true" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 100000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4.5rem 1rem 1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', width: 'min(580px, 96vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 50px rgba(15,23,42,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.85rem 1.1rem', borderBottom: '1px solid #e3e8ef' }}>
              <Bell size={16} />
              <span style={{ flex: 1, fontWeight: 700, color: '#102a43' }}>Notifications</span>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>last 14 days</span>
              <button type="button" onClick={() => setOpen(false)} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'inline-flex', padding: '0.2rem' }}><X size={18} /></button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem', padding: '0.35rem 0.8rem', borderBottom: '1px solid #eef2f7', flexShrink: 0 }}>
              {TABS.map((t) => {
                const active = tab === t.key;
                const u = tabUnseen(t.key);
                return (
                  <button key={t.key} type="button" onClick={() => setTab(t.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', border: 'none', cursor: 'pointer', padding: '0.3rem 0.6rem', borderRadius: '7px', fontSize: '0.82rem', fontWeight: 600, background: active ? '#eef4fb' : 'transparent', color: active ? '#1d4ed8' : '#64748b' }}>
                    {t.label}
                    {u > 0 && <span style={{ minWidth: '16px', height: '16px', padding: '0 4px', borderRadius: '999px', background: active ? '#1d4ed8' : '#cbd5e1', color: '#fff', fontSize: '0.62rem', fontWeight: 700, lineHeight: '16px', textAlign: 'center' }}>{u > 9 ? '9+' : u}</span>}
                  </button>
                );
              })}
              <span style={{ flex: 1 }} />
              <select value={visibility} onChange={(e) => setVisibility(e.target.value)} aria-label="Notification visibility" style={{ fontSize: '0.75rem', padding: '0.12rem 0.3rem', borderRadius: '6px', border: '1px solid #e3e8ef', color: '#334e68', background: '#fff', cursor: 'pointer' }}>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="all">All</option>
              </select>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={{ fontSize: '0.75rem', padding: '0.12rem 0.3rem', borderRadius: '6px', border: '1px solid #e3e8ef', color: '#334e68', background: '#fff', cursor: 'pointer' }}>
                <option value="unseen">Unseen first</option>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="property">Property</option>
              </select>
            </div>

            {/* List */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0.3rem 0' }}>
              {loading && items.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}><Loader2 size={16} className="cpm-spin" /> Loading…</div>
              ) : visible.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>No {visibility === 'all' ? '' : `${visibility} `}notifications in the last 14 days.</div>
              ) : (
                visible.map((n) => {
                  const res = isReservation(n.type);
                  const Icon = res ? (n.type === 'cancellation' ? CalendarX2 : CalendarPlus) : fileIconFor(n.file_name);
                  const iconColor = n.type === 'cancellation' ? '#e11d48' : n.type === 'reservation' ? '#0d9488' : '#64748b';
                  const onTitle = res ? () => openReservation(n) : () => openDocument(n);
                  const title = res
                    ? `${n.type === 'cancellation' ? 'Cancellation' : 'New booking'}${n.guest_name ? ` · ${n.guest_name}` : ''}`
                    : n.file_name;
                  const sub = res
                    ? `${n.property_name || ''}${n.channel ? ` · ${n.channel}` : ''}`
                    : `${n.property_name ? `${n.property_name} · ` : ''}${folderOf(n.rel_path)}`;
                  const meta = res
                    ? `${fmtDate(n.checkin)}–${fmtDate(n.checkout)} · ${fmtDateTime(n.detected_at)}`
                    : null;
                  return (
                    <div key={n.key}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.55rem 1.1rem', opacity: n.seen ? 0.55 : 1, borderTop: '1px solid #f4f6f9', background: 'transparent', transition: 'background 0.12s' }}>
                      <input type="checkbox" checked={!!n.seen} onChange={() => toggleSeen(n)} title={n.seen ? 'Mark as unseen' : 'Mark as seen'} style={{ marginTop: '0.25rem', cursor: 'pointer' }} />
                      <Icon size={18} strokeWidth={1.8} style={{ color: iconColor, flexShrink: 0, marginTop: '0.1rem' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <button type="button" onClick={onTitle} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1d4ed8', fontWeight: 600, fontSize: '0.875rem', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', display: 'block' }}>{title}</button>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
                        <div style={{ fontSize: '0.72rem', marginTop: '0.1rem' }}>
                          {res ? (
                            <span style={{ color: '#94a3b8' }}>{meta}</span>
                          ) : (
                            <>
                              <span style={{ color: CHANGE_COLOR[n.change_type] || '#64748b', fontWeight: 600 }}>{CHANGE_LABEL[n.change_type] || n.change_type}</span>
                              <span style={{ color: '#cbd5e1' }}> · </span>
                              <span style={{ color: '#94a3b8' }}>{fmtDateTime(n.detected_at)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ), document.body)}

      {preview && createPortal((
        <div role="dialog" aria-modal="true" onClick={closePreview} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 100001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isNarrow ? 0 : '2rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: isNarrow ? 0 : '10px', width: isNarrow ? '100%' : 'min(960px, 95vw)', height: isNarrow ? '100%' : 'min(90vh, 1000px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid #e3e8ef' }}>
              <span style={{ flex: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview.name}</span>
              <button type="button" onClick={closePreview} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'inline-flex', padding: '0.25rem' }}><X size={18} /></button>
            </div>
            <div style={{ flex: 1, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
              {preview.loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#64748b' }}><Loader2 size={18} className="cpm-spin" /> Loading preview…</span>
              ) : preview.error ? (
                <span style={{ color: '#b42318', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><AlertCircle size={16} /> {preview.error}</span>
              ) : preview.kind === 'pdf' ? (
                <iframe title={preview.name} src={preview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : preview.kind === 'video' ? (
                <video src={preview.url} controls playsInline preload="metadata" style={{ width: '100%', maxWidth: '100%', maxHeight: '100%' }} />
              ) : (
                <img alt={preview.name} src={preview.url} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      ), document.body)}
    </span>
  );
}
